/** Main menu scene — lists saved characters, accepts new character names, and navigates to BootScene. */
import Phaser from 'phaser';
import { fadeToScene } from '../utils/SceneTransitions';
import { SaveSystem } from '../systems/SaveSystem';
import { KeyBindings } from '../systems/KeyBindings';
import { crispText, crispButton, setupCrispCamera } from '../ui/CrispUtil';
import { Font, Hex, ButtonColor, applyButtonHover } from '../ui/Theme';
import { GAME_WIDTH, GAME_HEIGHT } from '../utils/Constants';

/**
 * Renders the title screen with a new-game button, per-character save slots with delete controls,
 * and a controls link. Spawns a DOM input element for character name entry.
 */
export class MenuScene extends Phaser.Scene {
  private saveSystem!: SaveSystem;
  private menuContainer!: Phaser.GameObjects.Container;
  private inputElement: HTMLInputElement | null = null;

  constructor() {
    super({ key: 'MenuScene' });
  }

  create(): void {
    this.saveSystem = new SaveSystem();
    KeyBindings.load();
    setupCrispCamera(this);
    const width = GAME_WIDTH;

    crispText(this, width / 2, 60, 'MAGZIGEDDON', {
      fontFamily: Font.family, fontSize: Font.sizeGameTitle, color: Hex.greenBright,
    }).setOrigin(0.5);

    crispText(this, width / 2, 116, 'survive or die trying', {
      fontFamily: Font.family, fontSize: Font.sizeBody, color: Hex.textMuted,
    }).setOrigin(0.5);

    this.menuContainer = this.add.container(0, 0);
    this.buildMenu();

    this.events.on('shutdown', () => this.removeInput());
  }

  private buildMenu(): void {
    this.menuContainer.removeAll(true);
    this.removeInput();

    const width = GAME_WIDTH;
    const saves = this.saveSystem.listSaves();
    let y = 180;

    // New Game button
    const newGameBtn = crispButton(this, width / 2, y, '[ NEW GAME ]',
      Font.sizeButton, ButtonColor.primary, () => this.showNameInput());

    this.menuContainer.add(newGameBtn);
    y += 60;

    if (saves.length > 0) {
      const header = crispText(this, width / 2, y, 'SAVED CHARACTERS', {
        fontFamily: Font.family, fontSize: '16px', color: Hex.textSecondary,
      }).setOrigin(0.5);
      this.menuContainer.add(header);
      y += 36;

      for (const name of saves) {
        const row = this.createSaveRow(name, width / 2, y);
        this.menuContainer.add(row);
        y += 36;
      }
    }

    // Controls button
    y += 20;
    const controlsBtn = crispButton(this, width / 2, y, '[ CONTROLS ]',
      Font.sizeButtonSm, ButtonColor.muted, () => this.scene.start('KeyRemapScene'));
    this.menuContainer.add(controlsBtn);
  }

  private createSaveRow(name: string, x: number, y: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const nameBtn = crispText(this, -40, 0, name, {
      fontFamily: Font.family, fontSize: Font.sizeButtonSm, color: ButtonColor.primary.default,
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    applyButtonHover(nameBtn, ButtonColor.primary);
    nameBtn.on('pointerdown', () => {
      fadeToScene(this, 'BootScene', { saveName: name, isNew: false });
    });

    const deleteBtn = crispText(this, nameBtn.x + nameBtn.displayWidth / 2 + 30, 0, '[x]', {
      fontFamily: Font.family, fontSize: '16px', color: ButtonColor.danger.default,
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    applyButtonHover(deleteBtn, ButtonColor.danger);
    deleteBtn.on('pointerdown', () => {
      this.saveSystem.deleteSave(name);
      this.buildMenu();
    });

    container.add([nameBtn, deleteBtn]);
    return container;
  }

  private showNameInput(): void {
    this.removeInput();

    const canvas = this.game.canvas;
    const rect = canvas.getBoundingClientRect();

    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Character name...';
    input.maxLength = 20;
    input.style.cssText = `
      position: absolute;
      left: ${rect.left + rect.width / 2 - 120}px;
      top: ${rect.top + rect.height / 2}px;
      width: 240px;
      padding: 8px 12px;
      font-family: monospace;
      font-size: 18px;
      color: #00ff00;
      background: #111;
      border: 2px solid #00cc00;
      outline: none;
      text-align: center;
    `;

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this.confirmName(input.value.trim());
      } else if (e.key === 'Escape') {
        this.removeInput();
      }
    });

    document.body.appendChild(input);
    input.focus();
    this.inputElement = input;

    // Add cancel hint
    const hint = crispText(this, GAME_WIDTH / 2, GAME_HEIGHT / 2 + 40, 'Enter to confirm · Esc to cancel', {
      fontFamily: Font.family, fontSize: Font.sizeTiny, color: Hex.textMuted,
    }).setOrigin(0.5).setName('input-hint');
    this.menuContainer.add(hint);
  }

  private confirmName(name: string): void {
    if (!name) return;

    const existing = this.saveSystem.listSaves();
    if (existing.includes(name)) {
      // Flash the input border red briefly
      if (this.inputElement) {
        this.inputElement.style.borderColor = '#ff0000';
        this.inputElement.placeholder = 'Name already taken!';
        this.inputElement.value = '';
        setTimeout(() => {
          if (this.inputElement) {
            this.inputElement.style.borderColor = '#00cc00';
            this.inputElement.placeholder = 'Character name...';
          }
        }, 1000);
      }
      return;
    }

    this.removeInput();
    fadeToScene(this, 'BootScene', { saveName: name, isNew: true });
  }

  private removeInput(): void {
    if (this.inputElement) {
      this.inputElement.remove();
      this.inputElement = null;
    }
    // Remove hint text if it exists
    const hint = this.menuContainer.getByName('input-hint');
    if (hint) {
      hint.destroy();
    }
  }
}
